"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { login } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  totpCode: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setError(null);
    try {
      const user = await login(data.email, data.password, data.totpCode);
      router.push(user.totpRequired
        ? "/auth/totp-setup"
        : user.passwordChangeRequired
          ? (user.role === "viewer" ? "/account" : "/admin/account")
          : "/admin");
      router.refresh();
    } catch (e) {
      if (e instanceof ApiError && e.errors?.code?.includes("TOTP_ENROLLMENT_REQUIRED")) {
        router.push("/auth/totp-setup");
        return;
      }
      setError(
        e instanceof ApiError ? e.message : "Invalid email or password",
      );
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
                {...register("email")}
              />
              {errors.email && <p id="email-error" className="text-sm text-red-600">{errors.email.message}</p>}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password ? "true" : "false"}
                aria-describedby={errors.password ? "password-error" : undefined}
                {...register("password")}
              />
              {errors.password && <p id="password-error" className="text-sm text-red-600">{errors.password.message}</p>}
            </div>
            <div>
              <Label htmlFor="totpCode">Authenticator code</Label>
              <Input
                id="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Required after setup"
                aria-invalid={errors.totpCode ? "true" : "false"}
                aria-describedby={errors.totpCode ? "totp-error" : undefined}
                {...register("totpCode")}
                onChange={(e) =>
                  setValue("totpCode", e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
              {errors.totpCode && <p id="totp-error" className="text-sm text-red-600">{errors.totpCode.message}</p>}
            </div>
            <div aria-live="polite" aria-atomic="true">
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm">
            <Link href="/auth/magic-link" className="text-[var(--color-primary)] hover:underline">
              Sign in with magic link
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
